<template>
  <div class="toast-container">
    <TransitionGroup name="toast" tag="div" class="toast-list">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        :class="[
          'toast',
          `toast-${toast.type}`,
          { 'toast-persistent': toast.persistent }
        ]"
        @click="!toast.persistent && removeToast(toast.id)"
      >
        <div class="toast-content">
          <!-- Icon -->
          <div class="toast-icon">
            <CheckCircle v-if="toast.type === 'success'" :size="20" />
            <AlertCircle v-else-if="toast.type === 'error'" :size="20" />
            <AlertTriangle v-else-if="toast.type === 'warning'" :size="20" />
            <Info v-else-if="toast.type === 'info'" :size="20" />
          </div>
          
          <!-- Content -->
          <div class="toast-body">
            <div class="toast-title">{{ toast.title }}</div>
            <div v-if="toast.message" class="toast-message">{{ toast.message }}</div>
          </div>
          
          <!-- Close button -->
          <button
            @click.stop="removeToast(toast.id)"
            class="toast-close"
            :aria-label="'Close notification'"
          >
            <X :size="16" />
          </button>
        </div>
        
        <!-- Progress bar for timed toasts -->
        <div
          v-if="!toast.persistent && toast.duration"
          class="toast-progress"
          :style="{ animationDuration: `${toast.duration}ms` }"
        ></div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script>
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from 'lucide-vue-next'
import { useToast } from '@/composables/useToast'

export default {
  name: 'ToastContainer',
  components: {
    CheckCircle,
    AlertCircle,
    AlertTriangle,
    Info,
    X
  },
  setup() {
    const { toasts, removeToast } = useToast()
    
    return {
      toasts,
      removeToast
    }
  }
}
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  pointer-events: none;
}

.toast-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 400px;
  width: 100%;
}

.toast {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  pointer-events: auto;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
}

.toast:hover {
  transform: translateX(-4px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.toast-persistent {
  cursor: default;
}

.toast-persistent:hover {
  transform: none;
}

.toast-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
}

.toast-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.toast-body {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-weight: 600;
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 2px;
}

.toast-message {
  font-size: 13px;
  line-height: 1.4;
  opacity: 0.8;
}

.toast-close {
  flex-shrink: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.2s ease;
  margin-top: 2px;
  padding: 0;
}

.toast-close:hover {
  opacity: 1;
}

.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
  animation: progress-bar linear forwards;
}

/* Toast type styles */
.toast-success {
  border-left: 4px solid #10b981 !important;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(255, 255, 255, 0.95) 100%) !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(16, 185, 129, 0.1) !important;
}

.toast-success .toast-icon {
  color: #059669 !important;
  filter: drop-shadow(0 1px 2px rgba(16, 185, 129, 0.3));
}

.toast-success .toast-progress {
  background-color: #10b981 !important;
}

.toast-success .toast-title {
  color: #065f46 !important;
}

.toast-error {
  border-left: 4px solid #ef4444 !important;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(255, 255, 255, 0.95) 100%) !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(239, 68, 68, 0.1) !important;
}

.toast-error .toast-icon {
  color: #dc2626 !important;
  filter: drop-shadow(0 1px 2px rgba(239, 68, 68, 0.3));
}

.toast-error .toast-progress {
  background-color: #ef4444 !important;
}

.toast-error .toast-title {
  color: #7f1d1d !important;
}

.toast-warning {
  border-left: 4px solid #f59e0b !important;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(255, 255, 255, 0.95) 100%) !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(245, 158, 11, 0.1) !important;
}

.toast-warning .toast-icon {
  color: #d97706 !important;
  filter: drop-shadow(0 1px 2px rgba(245, 158, 11, 0.3));
}

.toast-warning .toast-progress {
  background-color: #f59e0b !important;
}

.toast-warning .toast-title {
  color: #78350f !important;
}

.toast-info {
  border-left: 4px solid #3b82f6 !important;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(255, 255, 255, 0.95) 100%) !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(59, 130, 246, 0.1) !important;
}

.toast-info .toast-icon {
  color: #2563eb !important;
  filter: drop-shadow(0 1px 2px rgba(59, 130, 246, 0.3));
}

.toast-info .toast-progress {
  background-color: #3b82f6 !important;
}

.toast-info .toast-title {
  color: #1e3a8a !important;
}

/* Transition animations */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* Progress bar animation */
@keyframes progress-bar {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

/* Responsive design */
@media (max-width: 640px) {
  .toast-container {
    top: 10px;
    right: 10px;
    left: 10px;
  }
  
  .toast-list {
    max-width: none;
  }
}
</style>
